// The pricing workspace: open one request, price its lines, save, submit
// for approval. Keyboard-first (admin/CLAUDE.md): Tab moves down the price
// column, Enter commits the current line's edit and moves to the next row,
// Escape reverts the current field to its last-saved value — see
// handlePriceKeyDown below. Totals live in the table's own footer row, not
// a sidebar (admin/CLAUDE.md).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useWorkspace,
  useSaveLines,
  useSubmitQuotation,
  useUnsealQuotation,
} from '../hooks/useWorkspace.js';
import { useCatalogProducts } from '../hooks/useCatalogProducts.js';
import { useRequestUploads } from '../hooks/useRequestUploads.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import AttachmentPanel from '../components/AttachmentPanel.jsx';
import UploadMappingPanel from '../components/UploadMappingPanel.jsx';
import PricingLineRow from '../components/PricingLineRow.jsx';
import { SignatureElement } from 'shared/components/SignatureElement/index.js';
import { statusLabel } from '../i18n.js';
import {
  formatMoney,
  previewLineTotalCents,
  sumCents,
  centsToDecimalString,
} from 'shared/money.js';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { toDateInputValue, suggestValidUntil } from '../utils/editableTerms.js';
import { ApiError } from 'shared/api/client.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './WorkspacePage.module.css';

function buildInitialRows({ request, quotation }) {
  if (quotation && quotation.lines && quotation.lines.length > 0) {
    return quotation.lines.map((ql) => ({
      requestLineId: ql.request_line_id ?? undefined,
      productId: ql.product_id ?? undefined,
      description: ql.description,
      unit: ql.unit || 'pcs',
      qty: ql.qty !== null && ql.qty !== undefined ? String(ql.qty) : '',
      unitPrice: ql.unit_price !== null && ql.unit_price !== undefined ? String(ql.unit_price) : '',
      discountReason: ql.discount_reason || '',
      belowFloor: Boolean(ql.below_floor),
    }));
  }
  return request.lines.map((rl) => ({
    requestLineId: rl.id,
    productId: rl.matched_product_id ?? undefined,
    description: rl.raw_description,
    unit: rl.raw_unit || 'pcs',
    qty: rl.qty !== null && rl.qty !== undefined ? String(rl.qty) : '',
    unitPrice: '',
    discountReason: '',
    belowFloor: false,
  }));
}

// Our three document-terms fields (editable-terms task) — prefilled from
// the quotation's own saved values if it has any, else from the settings
// defaults getWorkspace returns. Deliberately separate from
// buildInitialRows above: these are header-level fields, not part of the
// line array react-hook-form manages.
function buildInitialTerms({ quotation, defaults }) {
  const hasDeliveryDays =
    quotation && quotation.delivery_days !== null && quotation.delivery_days !== undefined;
  return {
    paymentTerms:
      (quotation && quotation.payment_terms) || (defaults && defaults.paymentTerms) || '',
    deliveryDays: hasDeliveryDays
      ? String(quotation.delivery_days)
      : defaults
        ? String(defaults.deliveryDays)
        : '',
    validUntil:
      quotation && quotation.valid_until
        ? toDateInputValue(quotation.valid_until)
        : suggestValidUntil(defaults && defaults.quoteValidityDays),
  };
}

// Server error messages for BELOW_FLOOR_REASON_REQUIRED are formatted
// "Line {n}: ..." (services/quotationService.js#resolveLineFloorState),
// where n is the 1-based position in the array this workspace itself just
// submitted. This never renders the message text (CLAUDE.md: never show a
// raw error string from the server) — it only extracts the line index so
// the right row's discount-reason field can be focused/flagged with a
// localized message instead.
function parseLineNumberFromMessage(message) {
  const match = /^Line (\d+):/.exec(message || '');
  return match ? Number(match[1]) - 1 : null;
}

// --- Sealed-bid tender: read-only status once a sealed quotation has been
// submitted (status !== 'draft' — see the caller's own guard). Purely
// additive: the workspace has no other handling for a non-draft quotation
// status today, so this never touches the pricing-table rendering above it.
// Unsealing here is irreversible (no reseal path, migration 020) — the
// button disappears for good once `unsealed_at` is set.
function SealedStatusSection({ quotation, requestId }) {
  const { t, lang, confirm, toastOk, toastErr } = useActionFeedback();
  const unsealMutation = useUnsealQuotation(requestId);

  const handleUnseal = async () => {
    const ok = await confirm({
      title: t('workspace.seal.confirmUnsealTitle'),
      message: t('workspace.seal.confirmUnsealBody'),
      confirmLabel: t('workspace.seal.unsealNow'),
      tone: 'danger',
    });
    if (!ok) return;

    unsealMutation.mutate(undefined, {
      onSuccess: () => toastOk(t('workspace.seal.unsealedToast')),
      onError: (err) =>
        toastErr(err, [
          'QUOTATION_NOT_FOUND',
          'QUOTATION_NOT_SEALED',
          'QUOTATION_ALREADY_UNSEALED',
        ]),
    });
  };

  return (
    <section className={styles.sealSection}>
      <h2 className={styles.sealSectionTitle}>{t('workspace.seal.sectionTitle')}</h2>
      <p className={styles.sealMeta}>
        <strong>{t('workspace.seal.sealedLabel')}:</strong>{' '}
        <span className="tabularNums">{formatDateTime(quotation.sealed_at, lang) || '—'}</span>
      </p>
      {quotation.seal_bid_opening_at && (
        <p className={styles.sealMeta}>
          <strong>{t('workspace.seal.scheduledBidOpening')}:</strong>{' '}
          <span className="tabularNums">
            {formatDateTime(quotation.seal_bid_opening_at, lang) || '—'}
          </span>
        </p>
      )}
      {quotation.unsealed_at ? (
        <p className={styles.sealUnsealedNote}>
          {t('workspace.seal.unsealedNote', {
            date: formatDateTime(quotation.unsealed_at, lang) || '—',
          })}
        </p>
      ) : quotation.sealed_at ? (
        <button
          type="button"
          className={styles.unsealButton}
          onClick={handleUnseal}
          disabled={unsealMutation.isPending}
        >
          {unsealMutation.isPending && <Spinner />}{' '}
          {unsealMutation.isPending ? t('workspace.seal.unsealing') : t('workspace.seal.unsealNow')}
        </button>
      ) : (
        // sealed_at is only stamped once the quotation is actually sent
        // (services/quotationService.js#markSent) — while still pending
        // admin approval there is nothing to unseal yet, and the server
        // would reject an attempt with QUOTATION_NOT_SEALED. No button here,
        // matching that.
        <p className={styles.sealHelperText}>{t('workspace.seal.pendingSendNote')}</p>
      )}
    </section>
  );
}

export default function WorkspacePage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const workspaceQuery = useWorkspace(requestId);
  const uploadsQuery = useRequestUploads(requestId);
  const { data: productsMap } = useCatalogProducts();
  const saveLinesMutation = useSaveLines(requestId);
  const submitMutation = useSubmitQuotation(requestId);
  const { toast, toastOk, toastErr } = useActionFeedback();

  const { register, control, handleSubmit, setValue, getValues, reset, formState } = useForm({
    defaultValues: { lines: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });

  const [initializedFor, setInitializedFor] = useState(null);
  const lastSavedValuesRef = useRef([]);
  const priceInputsRef = useRef([]);

  // Sealed-bid tender (migration 020): submit-time-only inputs, not part of
  // the `lines` field array above — reset after a successful submit, same
  // as this page's other submit-scoped local state.
  const [sealed, setSealed] = useState(false);
  const [bidOpeningAt, setBidOpeningAt] = useState('');

  // Editable-terms task: OUR delivery/payment/validity terms — plain local
  // state, same "controlled string input, parsed only at save time" shape
  // SettingsPage.jsx's number fields already use, not part of react-hook-
  // form's `lines` array since these are header-level, not per-line.
  const [paymentTerms, setPaymentTerms] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  const [validUntil, setValidUntil] = useState('');

  useEffect(() => {
    if (workspaceQuery.data && initializedFor !== requestId) {
      const rows = buildInitialRows(workspaceQuery.data);
      reset({ lines: rows });
      lastSavedValuesRef.current = rows.map((r) => r.unitPrice);
      const terms = buildInitialTerms(workspaceQuery.data);
      setPaymentTerms(terms.paymentTerms);
      setDeliveryDays(terms.deliveryDays);
      setValidUntil(terms.validUntil);
      setInitializedFor(requestId);
    }
  }, [workspaceQuery.data, requestId, initializedFor, reset]);

  const requestLinesById = useMemo(() => {
    const map = new Map();
    if (workspaceQuery.data?.request?.lines) {
      workspaceQuery.data.request.lines.forEach((line) => map.set(line.id, line));
    }
    return map;
  }, [workspaceQuery.data]);

  const registerPriceInput = useCallback((index, el) => {
    priceInputsRef.current[index] = el;
  }, []);

  const focusPriceInput = useCallback((index) => {
    const el = priceInputsRef.current[index];
    if (el) el.focus();
  }, []);

  // Tab / Enter both move focus down the price column (root CLAUDE.md's
  // "twenty-line request without touching the mouse" requirement reads
  // naturally as a spreadsheet-style "commit and go to the next line" for
  // both keys — the distinct, explicit behaviour is Escape, which reverts
  // instead of advancing). On the LAST row this intentionally does NOT
  // intercept Tab — native tab order takes over from there (discount-reason
  // field, then Add line / Save / Submit), which keeps every field
  // reachable by keyboard without this component having to hardcode a
  // "final destination" element. Enter on the last row just blurs (commits
  // the edit) rather than guessing where focus should go next.
  const handlePriceKeyDown = useCallback(
    (event, index) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        if (index < fields.length - 1) {
          event.preventDefault();
          focusPriceInput(index + 1);
        }
        // Last row: let native Tab proceed to the Save button.
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (index < fields.length - 1) {
          focusPriceInput(index + 1);
        } else {
          event.currentTarget.blur();
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        const lastSaved = lastSavedValuesRef.current[index] ?? '';
        setValue(`lines.${index}.unitPrice`, lastSaved, {
          shouldDirty: false,
          shouldValidate: false,
        });
        event.currentTarget.blur();
      }
    },
    [fields.length, focusPriceInput, setValue]
  );

  const onSave = handleSubmit((values) => {
    const payload = values.lines
      .filter((line) => line.qty !== '' && line.unitPrice !== '')
      .map((line) => ({
        requestLineId: line.requestLineId,
        productId: line.productId,
        description: line.description,
        unit: line.unit,
        qty: line.qty,
        unitPrice: line.unitPrice,
        discountReason: line.discountReason ? line.discountReason.trim() || undefined : undefined,
      }));

    if (payload.length === 0) {
      toast.error(t('error.EMPTY_QUOTATION'));
      return;
    }

    // Editable-terms task: sent every save, alongside lines — see
    // useSaveLines' own comment for why that's one call, not two, and
    // quotationRepository.updateHeaderTerms for why an empty/untouched
    // field here never blanks out a previously-saved value.
    const body = {
      lines: payload,
      validUntil: validUntil || null,
      deliveryDays: deliveryDays !== '' ? deliveryDays : null,
      paymentTerms: paymentTerms.trim() !== '' ? paymentTerms.trim() : null,
    };

    saveLinesMutation.mutate(body, {
      onSuccess: (data) => {
        const rows = buildInitialRows(data);
        reset({ lines: rows });
        lastSavedValuesRef.current = rows.map((r) => r.unitPrice);
        const terms = buildInitialTerms(data);
        setPaymentTerms(terms.paymentTerms);
        setDeliveryDays(terms.deliveryDays);
        setValidUntil(terms.validUntil);
        toastOk(t('workspace.savedToast'));
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'BELOW_FLOOR_REASON_REQUIRED') {
          const lineIndex = parseLineNumberFromMessage(err.message);
          if (lineIndex !== null && lineIndex < fields.length) {
            const currentDiscount = getValues(`lines.${lineIndex}.discountReason`);
            // Scroll the offending price input into view and focus it —
            // the toast says what's wrong, this points at which line.
            if (!currentDiscount || !currentDiscount.trim()) focusPriceInput(lineIndex);
          }
        }
        toastErr(err, [
          'BELOW_FLOOR_REASON_REQUIRED',
          'EMPTY_QUOTATION',
          'REQUEST_NOT_PRICEABLE',
          'QUOTATION_NOT_EDITABLE',
        ]);
      },
    });
  });

  const onSubmitForApproval = () => {
    // Client-side sanity check only — avoids an obvious round trip. The
    // server (services/quotationService.js#submitForApproval) re-validates
    // this regardless.
    if (sealed && bidOpeningAt) {
      const parsed = new Date(bidOpeningAt);
      if (Number.isNaN(parsed.getTime()) || parsed <= new Date()) {
        toast.error(t('workspace.seal.scheduleMustBeFuture'));
        return;
      }
    }

    submitMutation.mutate(
      { sealed, bidOpeningAt: bidOpeningAt ? new Date(bidOpeningAt).toISOString() : null },
      {
        onSuccess: () => {
          toastOk(t('workspace.submittedToast'));
          setSealed(false);
          setBidOpeningAt('');
        },
        onError: (err) => {
          toastErr(err, [
            'EMPTY_QUOTATION',
            'REQUEST_NOT_PRICEABLE',
            'QUOTATION_NOT_EDITABLE',
            'SEAL_SCHEDULE_INVALID',
          ]);
        },
      }
    );
  };

  // useWatch (not getValues) so this page re-renders on every keystroke in
  // any line's qty/unitPrice — getValues() alone would read a snapshot that
  // is only current at the moment it's called, not reactive, so the footer
  // preview total would silently go stale between renders.
  const watchedLines = useWatch({ control, name: 'lines' });

  const previewSubtotalCents = useMemo(() => {
    const values = watchedLines || [];
    const cents = values
      .filter((l) => l && l.qty && l.unitPrice !== '' && l.unitPrice !== undefined)
      .map((l) => previewLineTotalCents(l.qty, l.unitPrice));
    return sumCents(cents);
  }, [watchedLines]);

  if (workspaceQuery.isLoading) {
    return <LoadingState variant="page" />;
  }
  if (workspaceQuery.isError) {
    return (
      <ErrorState
        messageId="workspace.errorBody"
        error={workspaceQuery.error}
        onRetry={workspaceQuery.refetch}
      />
    );
  }

  const { request, quotation } = workspaceQuery.data;
  const hasSavedLines = Boolean(quotation && quotation.lines && quotation.lines.length > 0);
  const proformaNo = quotation && quotation.proforma_no ? quotation.proforma_no : null;
  const quotationId = quotation ? quotation.id : null;

  const uploads = uploadsQuery.data || [];
  // At most one upload per request in this build, but it's a list. An upload
  // in 'rows_extracted' or 'mapped' still needs a salesperson to confirm its
  // column mapping before the worker can create line items.
  const mappingUpload = uploads.find(
    (u) => u.parseStatus === 'rows_extracted' || u.parseStatus === 'mapped'
  );
  const hasLines = request.lines.length > 0;
  // The mapping panel replaces the empty state — but only while there are no
  // lines yet. Once lines exist (mapped, or hand-priced) the pricing form
  // takes over.
  const showMappingPanel = Boolean(mappingUpload) && !hasLines;
  // Bare empty: no file, no lines. Still invites manual pricing (the form
  // below always renders) rather than dead-ending.
  const showBareEmpty = !hasLines && uploads.length === 0;

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={() => navigate('/')}>
        {t('workspace.backToQueue')}
      </button>

      <div className={styles.header}>
        <SignatureElement proformaNo={proformaNo} status="unallocated" lang={lang} />
        <div className={styles.headerMeta}>
          <p>
            <strong>{t('workspace.request')}:</strong>{' '}
            <span className="tabularNums">{request.ref_no}</span>
          </p>
          <p>
            <strong>{t('workspace.organization')}:</strong>{' '}
            {request.organization_name ??
              t('workspace.organizationFallback', { id: request.organization_id })}
          </p>
          <p>
            <strong>{t('workspace.neededBy')}:</strong>{' '}
            {formatDate(request.needed_by, lang) || t('queue.neededByNone')}
          </p>
          <p>
            <strong>{t('queue.col.status')}:</strong> {statusLabel(request.status, lang)}
          </p>
          {request.purpose && (
            <p>
              <strong>{t('workspace.purpose')}:</strong> {request.purpose}
            </p>
          )}
          {request.buyer_note && (
            <p>
              <strong>{t('workspace.buyerNote')}:</strong> {request.buyer_note}
            </p>
          )}
        </div>
      </div>

      <hr className="hairline" />

      {uploads.length > 0 && (
        <>
          <AttachmentPanel requestId={requestId} uploads={uploads} />
          <hr className="hairline" />
        </>
      )}

      {showMappingPanel && (
        <>
          <UploadMappingPanel requestId={requestId} upload={mappingUpload} />
          <hr className="hairline" />
        </>
      )}

      {showBareEmpty && (
        <EmptyState titleId="workspace.emptyLinesTitle" bodyId="workspace.emptyLinesBody" />
      )}

      {/* The pricing form always renders — a salesperson must be able to
          hand-price a request with zero lines (a file that produced none, or
          a request that never had a file). */}
      <form onSubmit={(e) => e.preventDefault()} className={styles.form}>
        {fields.length > 0 && <p className={styles.keyboardHint}>{t('workspace.keyboardHint')}</p>}

        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('workspace.col.line')}</th>
              <th>{t('workspace.col.description')}</th>
              <th>{t('workspace.col.unit')}</th>
              <th>{t('workspace.col.qty')}</th>
              <th>{t('workspace.col.product')}</th>
              <th>{t('workspace.col.unitPrice')}</th>
              <th>{t('workspace.col.lineTotal')}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <PricingLineRow
                key={field.id}
                index={index}
                field={field}
                register={register}
                control={control}
                setValue={setValue}
                errors={formState.errors}
                requestLine={field.requestLineId ? requestLinesById.get(field.requestLineId) : null}
                isNewLine={!field.requestLineId}
                productsMap={productsMap || new Map()}
                quotationId={quotationId}
                organizationTier={request.organization_tier}
                priceInputRef={registerPriceInput}
                onPriceKeyDown={handlePriceKeyDown}
                onRemove={() => remove(index)}
              />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} className={styles.footerLabel}>
                {t('workspace.footer.subtotalPreview')}
              </td>
              <td className={`${styles.footerTotal} tabularNums`}>
                {formatMoney(centsToDecimalString(previewSubtotalCents))}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>

        <p className={styles.vatNote}>{t('workspace.footer.vatNote')}</p>

        <hr className="hairline" />

        {/* Editable-terms task: OUR document terms — delivery lead time,
            payment terms, quote validity. Deliberately labelled "Our ..."
            throughout and placed in its own titled block, with the buyer's
            own needed-by date repeated right next to the delivery field in
            muted, explicitly-"Buyer asked for"-prefixed text — the two are
            easy to conflate (both are dates/timing about delivery) and
            mixing them up is a real money mistake (Ermiyas's own framing),
            so this is a side-by-side contrast, not just two numbers near
            each other. */}
        <div className={styles.termsBlock}>
          <h2 className={styles.termsTitle}>{t('workspace.terms.sectionTitle')}</h2>
          <div className={styles.termsFields}>
            <label className={styles.termsField}>
              {t('workspace.terms.deliveryDaysLabel')}
              <input
                type="number"
                className={`${styles.termsInput} tabularNums`}
                min={0}
                max={365}
                step="1"
                value={deliveryDays}
                onChange={(e) => setDeliveryDays(e.target.value)}
              />
              <span className={styles.termsBuyerAskedNote}>
                {t('workspace.terms.buyerAskedForNote', {
                  date: formatDate(request.needed_by, lang) || t('queue.neededByNone'),
                })}
              </span>
            </label>
            <label className={styles.termsField}>
              {t('workspace.terms.paymentTermsLabel')}
              <input
                type="text"
                className={styles.termsInput}
                maxLength={255}
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
              />
            </label>
            <label className={styles.termsField}>
              {t('workspace.terms.validUntilLabel')}
              <input
                type="date"
                className={`${styles.termsInput} tabularNums`}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </label>
          </div>
        </div>

        <hr className="hairline" />

        {/* Sealed-bid tender (migration 020): metadata about the whole
            quotation, deliberately separated from the line-pricing table
            above by a hairline rather than living inside it. */}
        <div className={styles.sealBlock}>
          <label className={styles.sealCheckboxLabel}>
            <input
              type="checkbox"
              checked={sealed}
              onChange={(e) => {
                setSealed(e.target.checked);
                if (!e.target.checked) setBidOpeningAt('');
              }}
            />
            {t('workspace.seal.checkboxLabel')}
          </label>
          <p className={styles.sealHelperText}>{t('workspace.seal.checkboxHelp')}</p>

          {sealed && (
            <div className={styles.sealScheduleField}>
              <label className={styles.sealScheduleLabel}>
                {t('workspace.seal.bidOpeningLabel')}
                <input
                  type="datetime-local"
                  className={`${styles.sealScheduleInput} tabularNums`}
                  value={bidOpeningAt}
                  onChange={(e) => setBidOpeningAt(e.target.value)}
                />
              </label>
              <p className={styles.sealHelperText}>{t('workspace.seal.bidOpeningHelp')}</p>
            </div>
          )}
        </div>

        <hr className="hairline" />

        <div className={styles.actionsRow}>
          <button
            type="button"
            className={styles.addLineButton}
            onClick={() =>
              append({
                requestLineId: undefined,
                productId: undefined,
                description: '',
                unit: 'pcs',
                qty: '',
                unitPrice: '',
                discountReason: '',
                belowFloor: false,
              })
            }
          >
            {t('workspace.actions.addLine')}
          </button>

          <div className={styles.primaryActions}>
            <button
              type="button"
              className={styles.saveButton}
              onClick={onSave}
              disabled={saveLinesMutation.isPending}
            >
              {saveLinesMutation.isPending && <Spinner />}{' '}
              {saveLinesMutation.isPending
                ? t('workspace.actions.saving')
                : t('workspace.actions.save')}
            </button>

            <button
              type="button"
              className={styles.submitButton}
              onClick={onSubmitForApproval}
              disabled={!hasSavedLines || submitMutation.isPending}
              title={!hasSavedLines ? t('workspace.actions.submitDisabledNoLines') : undefined}
            >
              {submitMutation.isPending && <Spinner />}{' '}
              {submitMutation.isPending
                ? t('workspace.actions.submitting')
                : t('workspace.actions.submit')}
            </button>
          </div>
        </div>
      </form>

      {/* Sealed-bid tender status: only once the quotation has actually been
          submitted/sent (not still a draft) — purely additive, the pricing
          form above renders exactly as it did before this feature. */}
      {quotation && Boolean(quotation.sealed) && quotation.status !== 'draft' && (
        <>
          <hr className="hairline" />
          <SealedStatusSection quotation={quotation} requestId={requestId} />
        </>
      )}
    </div>
  );
}
